import { DuplicateUndoStackDetailsPayLoad, GetDuplicateCitationPayLoad, GetUserPayload, MarkCitationAsDuplicatePayload } from "@/store/modules/citation/citation.types";
import { CitationDuplicateCountPayload, CitationPrimaryReferencePayload, CitationScreeningCountPayload, UpdateCitationStatusPayload } from "@/store/modules/duplicate-citation/duplicate-citation.types";
import { StudyScreeningType } from "@/store/modules/screening/screening.types";
import axios from "axios";

export const citationApi = {
  getUser({ userId }: GetUserPayload) {
    return axios.get('users', { params: { userId } });
  },

  getDuplicateCitation({ projectId, page, perPage, search }: GetDuplicateCitationPayLoad) {
    return axios.get(`projects/${projectId}/studies/duplicate-citations`, { params: { page, perPage, search } });
  },
  // JIRA: SUMDEV-2964 - New cluster-based duplicate citations API
  getDuplicateCitationV2({ projectId, page, perPage, search }: GetDuplicateCitationPayLoad) {
    return axios.get(`projects/${projectId}/studies/duplicate-citations-v2`, { params: { page, perPage, search } });
  },
  getPossibleDuplicateCitations({ projectId, page, perPage, search }: GetDuplicateCitationPayLoad) {
    return axios.get(`projects/${projectId}/studies/possible-duplicate-citations`, { params: { page, perPage, search } });
  },
  // JIRA: SUMDEV-2964 - New cluster-based possible duplicates API
  getPossibleDuplicateCitationsV2({ projectId, page, perPage, search }: GetDuplicateCitationPayLoad) {
    return axios.get(`projects/${projectId}/studies/possible-duplicate-citations-v2`, { params: { page, perPage, search } });
  },
  // JIRA: SUMDEV-2964 - New cluster-based duplicate counts by file API
  getClusterBasedDuplicateCountsByFile(projectId: number) {
    return axios.get(`projects/${projectId}/studies/get-cluster-based-duplicate-counts-by-file`);
  },
    // JIRA: SUMDEV-2964 - Get detailed comparison information between two citations
    getCitationComparisonDetails(projectId: number, primaryId: number, secondaryId: number) {
        return axios.get(`projects/${projectId}/studies/citation-comparison/${primaryId}/${secondaryId}`);
    },

    // JIRA: SUMDEV-2964 - Get detailed completeness score breakdown for a citation
    getCitationCompletenessDetails(projectId: number, citationId: number) {
        return axios.get(`projects/${projectId}/studies/citation-completeness/${citationId}`);
    },
  markCitationAsDuplicate({ projectId, ...payload }: MarkCitationAsDuplicatePayload) {
    return axios.post(`projects/${projectId}/studies/mark-citation-as-duplicate`, payload);
  },
  checkDuplicateScreeningStatus(projectId: number, citationIds: number[], screeningStage: StudyScreeningType) {
    return axios.get(`projects/${projectId}/studies/check-duplicate-screening-status`, {
      params: { citationIds: citationIds.join(','), screeningStage }
    });
  },
  // JIRA: SUMDEV-2964 - New API for marking citations as manually marked exact duplicates
  markCitationAsManuallyMarkedDuplicate(projectId: number, citationIds: number[], reason?: string, sourceStage?: string) {
    return axios.post(`projects/${projectId}/studies/mark-citation-as-manually-marked-duplicate`, {
      citationIds,
      reason,
      sourceStage
    });
  },
  // JIRA: SUMDEV-2964 - New API for getting manually marked duplicate citations v2
  getManuallyMarkedDuplicateCitationsV2({ projectId, page, perPage, search }: GetDuplicateCitationPayLoad) {
    return axios.get(`projects/${projectId}/studies/manually-marked-duplicate-citations-v2`, { 
      params: { page, perPage, search } 
    });
  },

  // JIRA: SUMDEV-2964 - New API for marking citations as not duplicate
  markCitationAsNotDuplicate(projectId: number, citationIds: number[], reason?: string, sourceStage?: string) {
    return axios.post(`projects/${projectId}/studies/mark-citation-as-not-duplicate`, {
      citationIds,
      reason,
      sourceStage
    });
  },

  // JIRA: SUMDEV-2964 - New API for replacing primary reference with cluster-based logic
  replacePrimaryReferenceV2(projectId: number, currentPrimaryCitationId: number, newPrimaryCitationId: number, clusterId: string) {
    return axios.post(`projects/${projectId}/studies/replace-primary-reference-v2`, {
      currentPrimaryCitationId,
      newPrimaryCitationId,
      clusterId
    });
  },

  // JIRA: SUMDEV-2964 - New API for replacing primary reference in possible duplicate clusters
  replacePrimaryReferencePossibleV2(projectId: number, currentPrimaryCitationId: number, newPrimaryCitationId: number, clusterId: string) {
      return axios.post(`projects/${projectId}/studies/replace-primary-reference-possible-v2`, {
          currentPrimaryCitationId,
          newPrimaryCitationId,
          clusterId
      });
  },

  // JIRA: SUMDEV-2964 - New API for undo file import with cluster-based logic
  undoFileImportV2(projectId: number, fileId: number) {
      return axios.delete(`projects/${projectId}/studies/undo-file-import-v2/${fileId}`);
  },
  
  updateCitationStatus(updateCitationStatusPayload: UpdateCitationStatusPayload) {
    const projectId = updateCitationStatusPayload.projectId;
    return axios.post(`projects/${projectId}/studies/update-citation-duplicate-status`, updateCitationStatusPayload);
  },
  replacePrimaryReference(payload: CitationPrimaryReferencePayload) {
    const projectId = payload.projectId;
    return axios.post(`projects/${projectId}/studies/replace-primary-reference`, payload)
  },
  getDuplicateCount({ projectId, duplicateStatus, fileId }: CitationDuplicateCountPayload) {
    return axios.get(`projects/${projectId}/studies/get-duplicate-citation-count`, { params: { duplicateStatus } });
  },
  getDuplicateCitationCountByFileId({ projectId, duplicateStatus, fileId }: CitationDuplicateCountPayload) {
    return axios.get(`projects/${projectId}/studies/get-duplicate-citation-count-by-file`, { params: { fileId, duplicateStatus } });
  },
  getManuallyMarkedDuplicateCitations({ projectId, page, perPage, search }: GetDuplicateCitationPayLoad) {
    return axios.get(`projects/${projectId}/studies/manually-marked-duplicate-citations`, { params: { page, perPage, search } });
  },
  getDuplicateCitationGroupCount(projectId: number) {
    return axios.get(`projects/${projectId}/studies/duplicate-group-count`);
  },
  getUndoStackDetails({ projectId, screen }: DuplicateUndoStackDetailsPayLoad) {
    return axios.get(`projects/${projectId}/studies/undo-stack-details`, { params: { screen } });
  },
  // JIRA: SUMDEV-2964
  /**
   * JIRA: SUMDEV-2964
   * Get V2 undo stack details for cluster-based undo
   */
  getUndoStackDetailsV2({ projectId, screen }: { projectId: number; screen: 'SYSTEM_DETECTED' | 'MANUALLY_REMOVED' | 'POSSIBLE_DUPLICATES_REMOVED' }) {
    return axios.get(`projects/${projectId}/studies/undo-stack-details-v2`, { params: { screen } });
  },
  revertCitationDuplicationStatuses({ projectId, screen }: DuplicateUndoStackDetailsPayLoad) {
    return axios.patch(`projects/${projectId}/studies/undo?screen=${screen}`);
  },
  // JIRA: SUMDEV-2964
  /**
   * JIRA: SUMDEV-2964
   * Undo cluster-based "Mark as Not Duplicate" operation (V2)
   * Restores citations to their previous cluster state using snapshots
   */
  revertCitationDuplicationStatusesV2({ projectId, screen }: { projectId: number; screen: 'SYSTEM_DETECTED' | 'MANUALLY_REMOVED' | 'POSSIBLE_DUPLICATES_REMOVED' }) {
    return axios.patch(`projects/${projectId}/studies/undo-v2?screen=${screen}`);
  },
  getScreeningCountByFile({ projectId, studyScreeningStatus }: CitationScreeningCountPayload) {
    return axios.get(`projects/${projectId}/studies/get-screening-count-by-file`, { params: { studyScreeningStatus } });
  },
}